import { useState, useEffect, useCallback, useRef } from "react";
import Vapi from "@vapi-ai/web";

type CallStatus = "idle" | "connecting" | "active" | "ended";

interface UseVapiReturn {
  startCall: (assistantId: string) => void;
  endCall: () => void;
  sendUserMessage: (message: string) => void;
  status: CallStatus;
  isCallActive: boolean;
  vapiInstance: Vapi | null;
  onDataCollected?: (data: any) => void;
}

// Global singleton to prevent duplicate VAPI instances
let globalVapiInstance: Vapi | null = null;
let isInitializing = false;

// Function to safely destroy VAPI instance and DailyIframe
function destroyVapiInstance(instance: Vapi | null) {
  if (instance) {
    try {
      // Stop the call first
      instance.stop();
    } catch (error) {
      console.warn("Error stopping VAPI instance:", error);
    }
  }
  
  // Always try to remove ALL DailyIframe elements from DOM (be aggressive)
  try {
    // Find and remove any DailyIframe elements (multiple selector strategies)
    const selectors = [
      'iframe[src*="daily"]',
      'iframe[src*="daily.co"]',
      'iframe[id*="daily"]',
      'iframe[class*="daily"]'
    ];
    
    selectors.forEach(selector => {
      try {
        const iframes = document.querySelectorAll(selector);
        iframes.forEach((iframe) => {
          try {
            iframe.remove();
          } catch (e) {
            console.warn("Error removing iframe:", e);
          }
        });
      } catch (e) {
        console.warn(`Error querying selector ${selector}:`, e);
      }
    });
    
    // Also check for DailyIframe in shadow DOM (if any)
    try {
      const allIframes = document.querySelectorAll('iframe');
      allIframes.forEach((iframe) => {
        try {
          const src = iframe.src || iframe.getAttribute('src') || '';
          if (src.includes('daily')) {
            iframe.remove();
          }
        } catch (e) {
          // Ignore errors for individual iframes
        }
      });
    } catch (e) {
      // Ignore errors
    }
  } catch (error) {
    console.warn("Error cleaning up DailyIframe:", error);
  }
}

// Function to ensure no DailyIframe exists before creating new instance
function ensureNoDailyIframe() {
  destroyVapiInstance(null); // Just clean up DOM, no instance needed
  // Wait a bit to ensure DOM cleanup is complete
  return new Promise(resolve => setTimeout(resolve, 300));
}

// Hardcoded fallback keys for production (VITE_ env vars don't work in deployed builds)
const VAPI_PUBLIC_KEY_FALLBACK = "b9f9163d-fff0-4512-b035-e3b0101c2e3e";
const VAPI_ASSISTANT_ID_FALLBACK = "d5c93c3d-c10a-4c0a-8d07-80d670970070";

// Function to set up data collection event listeners
function setupDataCollectionListeners(vapi: Vapi, onDataCollected?: (data: any) => void) {
  if (!onDataCollected) return;

  // VAPI emits everything as "message" events with a nested "type" property
  // We need to listen to "message" events and process based on the nested type
  vapi.on("message", (event: any) => {
    console.log("📨 VAPI message event:", event);
    
    // Process transcript events (most important for data extraction)
    if (event?.type === "transcript" && event.transcript) {
      console.log("📝 Processing transcript:", event.transcript);
      onDataCollected({
        type: "transcript",
        role: event.role,
        transcriptType: event.transcriptType, // 'partial' or 'final'
        transcript: event.transcript,
        ...event
      });
    }

    // Process conversation-update events (contains full conversation)
    if (event?.type === "conversation-update" && event.conversation) {
      console.log("💬 Processing conversation update");
      // Extract all transcripts from conversation
      const allTranscripts = event.conversation
        .filter((msg: any) => msg.role && msg.content)
        .map((msg: any) => `${msg.role}: ${msg.content}`)
        .join(" ");
      
      if (allTranscripts) {
        onDataCollected({
          type: "conversation-update",
          conversation: event.conversation,
          fullTranscript: allTranscripts,
          ...event
        });
      }
    }

    // Process function-call events
    if (event?.type === "function-call" && event.functionCall) {
      console.log("🔧 Processing function-call:", event.functionCall);
      onDataCollected({
        type: "function-call",
        functionName: event.functionCall.name,
        parameters: event.functionCall.parameters,
        ...event
      });
    }

    // Process function-call-result events
    if (event?.type === "function-call-result" && event.result) {
      console.log("✅ Processing function-call-result:", event.result);
      onDataCollected({
        type: "function-call-result",
        result: event.result,
        ...event
      });
    }

    // Process voice-input events (assistant's voice input confirmation)
    if (event?.type === "voice-input" && event.input) {
      console.log("🎤 Processing voice-input:", event.input);
      onDataCollected({
        type: "voice-input",
        input: event.input,
        ...event
      });
    }
  });

  // Note: VAPI SDK v2 primarily uses "message" events with nested types
  // The individual event types like "function-call", "transcript" etc. are handled above via the "message" listener
}

export function useVapi(onDataCollected?: (data: any) => void): UseVapiReturn {
  const [status, setStatus] = useState<CallStatus>("idle");
  const vapiRef = useRef<Vapi | null>(null);

  useEffect(() => {
    const publicKey = import.meta.env.VITE_VAPI_PUBLIC_KEY || VAPI_PUBLIC_KEY_FALLBACK;

    if (!publicKey) {
      console.error("VAPI Public Key is not configured");
      return;
    }

    // If we already have a global instance, reuse it
    if (globalVapiInstance) {
      vapiRef.current = globalVapiInstance;
      // Set up event listeners on the existing instance (in case they were removed)
      globalVapiInstance.on("call-start", () => {
        setStatus("active");
      });
      globalVapiInstance.on("call-end", () => {
        const event = {} as any;
        console.log("📞 Call ended event:", event);
        setStatus("ended");
        // Try to extract data from call-end event if available
        if (onDataCollected) {
          onDataCollected({
            type: "call-end"
          });
        }
        setTimeout(() => setStatus("idle"), 1000);
      });
      globalVapiInstance.on("error", (error) => {
        console.error("VAPI error:", error);
        setStatus("idle");
      });
      globalVapiInstance.on("call-start-failed", (event) => {
        console.error("Call start failed:", event);
        setStatus("idle");
        alert(`Failed to start call: ${event.error}. Please check your VAPI assistant configuration.`);
      });
      
      // Add data collection event listeners
      setupDataCollectionListeners(globalVapiInstance, onDataCollected);
      
      return;
    }

    // If already initializing, wait and try again
    if (isInitializing) {
      const checkInterval = setInterval(() => {
        if (!isInitializing && globalVapiInstance) {
          vapiRef.current = globalVapiInstance;
          clearInterval(checkInterval);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }

    // Clean up any existing instance in ref (shouldn't happen, but just in case)
    if (vapiRef.current && vapiRef.current !== globalVapiInstance) {
      destroyVapiInstance(vapiRef.current);
      vapiRef.current = null;
    }

    // Initialize VAPI client (singleton pattern)
    isInitializing = true;
    
    // Double-check no instance exists (race condition protection)
    if (globalVapiInstance) {
      vapiRef.current = globalVapiInstance;
      isInitializing = false;
      return;
    }

    // Clean up any DailyIframe elements synchronously before creating new instance
    destroyVapiInstance(null); // Clean up DOM only
    
    try {
      const vapi = new Vapi(publicKey);
      vapiRef.current = vapi;
      globalVapiInstance = vapi;
      isInitializing = false;

      // Set up event listeners
      vapi.on("call-start", () => {
        setStatus("active");
      });

      vapi.on("call-end", () => {
        const event = {} as any;
        console.log("📞 Call ended event:", event);
        setStatus("ended");
        // Try to extract data from call-end event if available
        if (onDataCollected) {
          onDataCollected({
            type: "call-end"
          });
        }
        // Reset to idle after a brief delay
        setTimeout(() => setStatus("idle"), 1000);
      });

      vapi.on("error", (error) => {
        console.error("VAPI error:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
        setStatus("idle");
      });

      vapi.on("call-start-failed", (event) => {
        console.error("Call start failed:", event);
        console.error("Error:", event.error);
        console.error("Stage:", event.stage);
        setStatus("idle");
        alert(`Failed to start call: ${event.error}. Please check your VAPI assistant configuration.`);
      });

      // Set up data collection event listeners
      setupDataCollectionListeners(vapi, onDataCollected);
    } catch (error) {
      console.error("Error creating VAPI instance:", error);
      isInitializing = false;
      globalVapiInstance = null;
      vapiRef.current = null;
    }

    // Cleanup on unmount - but don't destroy global instance if other components might use it
    return () => {
      // Only clean up the ref, keep global instance alive for other component instances
      vapiRef.current = null;
    };
  }, []);

  const startCall = useCallback(async (assistantId: string) => {
    // Ensure VAPI instance is ready
    if (!vapiRef.current) {
      console.error("VAPI client not initialized");
      // Try to wait a bit and check again
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!vapiRef.current) {
        alert("VAPI client is not ready. Please refresh the page and try again.");
        return;
      }
    }

    // Check if there's already an active call and stop it first
    if (status === "active" || status === "connecting") {
      try {
        vapiRef.current.stop();
        // Wait a bit before starting new call
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.warn("Error stopping existing call:", error);
      }
    }
    
    // Ensure we're using the correct instance (prevent duplicate)
    if (globalVapiInstance && globalVapiInstance !== vapiRef.current) {
      try {
        globalVapiInstance.stop();
      } catch (error) {
        console.warn("Error stopping global instance:", error);
      }
      vapiRef.current = globalVapiInstance;
    }

    const assistantIdEnv = import.meta.env.VITE_VAPI_ASSISTANT_ID || VAPI_ASSISTANT_ID_FALLBACK || assistantId;

    if (!assistantIdEnv) {
      console.error("Assistant ID is required");
      alert("Assistant ID is not configured.");
      return;
    }

    const publicKey = import.meta.env.VITE_VAPI_PUBLIC_KEY || VAPI_PUBLIC_KEY_FALLBACK;
    if (!publicKey) {
      console.error("VAPI Public Key is not set");
      alert("VAPI Public Key is not configured.");
      return;
    }

    console.log("Starting VAPI call with assistant ID:", assistantIdEnv);
    console.log("VAPI instance ready:", !!vapiRef.current);
    console.log("VAPI instance has start method:", typeof vapiRef.current.start === "function");
    console.log("Public key configured:", !!publicKey);
    console.log("Assistant ID:", assistantIdEnv);
    
    setStatus("connecting");
    
    try {
      const result = await vapiRef.current.start(assistantIdEnv);
      console.log("VAPI call started successfully, result:", result);
      if (!result) {
        console.error("Failed to start call - no result returned");
        setStatus("idle");
        alert("Failed to start call. Please check your VAPI configuration and assistant ID.");
      }
    } catch (error: any) {
      console.error("Failed to start call - error details:", error);
      console.error("Error type:", typeof error);
      console.error("Error keys:", error ? Object.keys(error) : "no error object");
      console.error("Error message:", error?.message);
      console.error("Error error:", error?.error);
      setStatus("idle");
      const errorMessage = error?.message || error?.error?.message || error?.toString() || "Unknown error";
      
      // Handle duplicate instance error specifically - clean up and retry
      if (errorMessage.includes("Duplicate") || errorMessage.includes("Dailyframe") || errorMessage.includes("DailyIframe")) {
        console.warn("Duplicate DailyIframe detected, destroying all instances and retrying...");
        
        // Destroy all instances and wait
        destroyVapiInstance(globalVapiInstance);
        destroyVapiInstance(vapiRef.current);
        globalVapiInstance = null;
        vapiRef.current = null;
        
        // Wait longer to ensure DailyIframe is fully removed from DOM
        setTimeout(() => {
          const publicKey = import.meta.env.VITE_VAPI_PUBLIC_KEY || VAPI_PUBLIC_KEY_FALLBACK;
          if (publicKey && !isInitializing) {
            isInitializing = true;
            const newVapi = new Vapi(publicKey);
            vapiRef.current = newVapi;
            globalVapiInstance = newVapi;
            isInitializing = false;
            
            // Set up event listeners again
            newVapi.on("call-start", () => setStatus("active"));
            newVapi.on("call-end", () => {
              setStatus("ended");
              setTimeout(() => setStatus("idle"), 1000);
            });
            newVapi.on("error", (error) => {
              console.error("VAPI error:", error);
              setStatus("idle");
            });
            newVapi.on("call-start-failed", (event) => {
              console.error("Call start failed:", event);
              setStatus("idle");
              const errorMsg = event?.error || "Unknown error";
              if (!errorMsg.includes("Duplicate") && !errorMsg.includes("Dailyframe") && !errorMsg.includes("DailyIframe")) {
                alert(`Failed to start call: ${errorMsg}. Please check your VAPI assistant configuration.`);
              }
            });
            
            // Set up data collection event listeners
            setupDataCollectionListeners(newVapi, onDataCollected);
            
            // Retry the call after a delay
            setTimeout(() => {
              if (vapiRef.current) {
                setStatus("connecting");
                vapiRef.current.start(assistantIdEnv).catch((err) => {
                  console.error("Retry failed:", err);
                  setStatus("idle");
                  const retryError = err?.message || err?.error?.message || "Unknown error";
                  if (!retryError.includes("Duplicate") && !retryError.includes("Dailyframe") && !retryError.includes("DailyIframe")) {
                    alert(`Failed to start call: ${retryError}. Please verify your VAPI credentials.`);
                  }
                });
              }
            }, 500);
          }
        }, 2000); // Longer delay to ensure cleanup
      } else {
        alert(`Failed to start call: ${errorMessage}. Please verify your VAPI credentials and assistant ID.`);
      }
    }
  }, [status]);

  const endCall = useCallback(() => {
    if (vapiRef.current) {
      vapiRef.current.stop();
      setStatus("idle");
    }
  }, []);

  const sendUserMessage = useCallback((message: string) => {
    if (vapiRef.current && (status === "active" || status === "connecting")) {
      try {
        console.log("📤 Sending user message to VAPI:", message);
        const vapi = vapiRef.current as any;
        
        // Try multiple possible VAPI SDK methods for sending user messages
        if (typeof vapi.send === "function") {
          vapi.send(message);
          console.log("✅ Message sent via send() method");
        } else if (typeof vapi.userMessage === "function") {
          vapi.userMessage(message);
          console.log("✅ Message sent via userMessage() method");
        } else if (typeof vapi.sendMessage === "function") {
          vapi.sendMessage(message);
          console.log("✅ Message sent via sendMessage() method");
        } else if (typeof vapi.speak === "function") {
          // If speak method exists, we might need to simulate user input differently
          // For now, log available methods to help debug
          console.warn("⚠️ Direct message sending methods not found");
          console.log("Available VAPI methods:", Object.keys(vapi).filter(key => typeof vapi[key] === "function"));
          
          // Alternative approach: Emit a user message event that VAPI can listen to
          // This would require VAPI to be configured to listen for user input events
          if (vapi._events && vapi.emit) {
            vapi.emit("user-message", { message, role: "user" });
            console.log("✅ Message sent via emit('user-message')");
          }
        } else {
          console.warn("⚠️ No message sending method found on VAPI instance");
        }
      } catch (error) {
        console.error("❌ Error sending user message:", error);
      }
    } else {
      console.warn("⚠️ Cannot send message - VAPI not active (status:", status, ")");
    }
  }, [status]);

  return {
    startCall,
    endCall,
    sendUserMessage,
    status,
    isCallActive: status === "active" || status === "connecting",
    vapiInstance: vapiRef.current,
    onDataCollected,
  };
}

